import { ExportGapFilterType } from "@app/exports/enumerations";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { FC, useContext, useMemo } from "react";
import { UserContext } from "@app/user/UserContext";
import { ReportCustomizationReducerActions } from "@app/report-customization/build-reports/interfaces/reportCustomizationReducerActionsEnum";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { ReportCustomizationContext } from "@app/report-customization/build-reports/ReportCustomizationContext";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { getSpecialtyGapTypes } from "@app/report-customization/build-reports/helpers/helpers";

export const SpecialtySection: FC = () => {
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const rcContext = useContext(ReportCustomizationContext);
  const user = useContext(UserContext);
  const isChangedGapsReport = !!rcContext.state.changedGapsType;
  const isNetNewProvider = rcContext.state.providerSource === ProviderNetworkTypeEnum.NetNew;
  const disableCurrentSpecialtySection = isChangedGapsReport || isNetNewProvider;

  const isZipAnalysisReport = rcContext.state.reportType === ReportType.ZipAnalysisResultsReport;
  const isZipGapResolutionStatusEnabled = enabledFeatureFlags.has(Features.ZIP_GAP_RESOLUTION_STATUS);

  const specialtyGapTypesOptions = useMemo(() => {
    const options = getSpecialtyGapTypes(user.roles, isZipAnalysisReport, isZipGapResolutionStatusEnabled);
    return options.map((o) => <option key={o.key} value={o.key}>{o.value}</option>);
  }, [user.roles, isZipAnalysisReport, isZipGapResolutionStatusEnabled]);

  const allSpecialtiesSelected = useMemo(() => {
    const allSpecialties = rcContext.state.allSpecialties.map((specialty) => specialty.key);
    return allSpecialties.length === rcContext.state.selectedSpecialties.length && allSpecialties.every((specialtyKey) => rcContext.state.selectedSpecialties.find((s) => s.key === specialtyKey));
  }, [rcContext.state.selectedSpecialties, rcContext.state.allSpecialties]);

  return (
    <>
      <div className="row mt-3">
        <h2 className="header-s col-auto">Specialty</h2>
      </div>
      <div className="row">
        <MultiselectDropdown
          containerClassName="col-4"
          customSelectInputText={allSpecialtiesSelected ? "All Specialties" : undefined}
          items={rcContext.state.allSpecialties}
          label="Specialty Groups"
          selectedItems={rcContext.state.selectedSpecialties}
          onSelectionChanged={(newSelections) => rcContext.dispatch({ payload: { selectedSpecialties: newSelections }, type: ReportCustomizationReducerActions.ChangeSelectedSpecialties })}
          disabled={rcContext.state.allSpecialties.length === 0 || disableCurrentSpecialtySection}
          testId="report-customization__specialties"
        />

        {rcContext.state.reportType !== ReportType.ZipAnalysisAdequacyServicingProvider
          && rcContext.state.reportType !== ReportType.ZipClassAggregateResultsReport
          && (rcContext.state.reportType !== ReportType.ZipAnalysisResultsReport || enabledFeatureFlags.has(Features.ZIP_EXCEPTIONS) || isZipGapResolutionStatusEnabled)
          && (
            <QESSelect
              containerClassName="col-4"
              label="Specialty Gaps"
              value={rcContext.state.specialtyGapsType === ExportGapFilterType.NotMeetingAccess ? ExportGapFilterType.Gaps : rcContext.state.specialtyGapsType}
              onChange={(e) => rcContext.dispatch({ payload: { specialtyGapsType: e.target.value as ExportGapFilterType }, type: ReportCustomizationReducerActions.ChangeSpecialtyGapsType })}
              disabled={disableCurrentSpecialtySection}
              testId="report-customization__specialty-gaps"
            >
              {specialtyGapTypesOptions}
            </QESSelect>
          )}

        {/* This replaces and controls both the service area and specialty gap type dropdowns - checked means not meeting access, unchecked means all */}
        {/* Since the NCP report was sending all specialty groups instead only the ones not meeting access (intent of NCP reports), a check on the stateProviderSource was added to checked/disabled*/ }
        {(rcContext.state.reportType === ReportType.ZipClassAggregateResultsReport
          || (rcContext.state.reportType === ReportType.ZipAnalysisResultsReport
            && !enabledFeatureFlags.has(Features.ZIP_EXCEPTIONS)
            && !isZipGapResolutionStatusEnabled)
          || (rcContext.state.reportType === ReportType.ZipAnalysisAdequacyServicingProvider))
          && (
            <QESCheckbox
              containerClassName="col-auto align-self-end"
              label="Only show specialties not meeting access"
              checked={rcContext.state.serviceAreaGapsType === ExportGapFilterType.NotMeetingAccess && rcContext.state.specialtyGapsType === ExportGapFilterType.NotMeetingAccess
                || rcContext.state.providerSource === ProviderNetworkTypeEnum.NextClosestProviders}
              onChange={(e) => {
                const newValue = e.target.checked ? ExportGapFilterType.NotMeetingAccess : ExportGapFilterType.All;
                rcContext.dispatch({ payload: { serviceAreaGapsType: newValue, specialtyGapsType: newValue }, type: ReportCustomizationReducerActions.ChangeOnlyShowSpecialtiesNotMeetingAccess })
              }}
              disabled={!rcContext.state.timeslice || rcContext.state.providerSource === ProviderNetworkTypeEnum.NextClosestProviders}
              testId="report-customization__only-specialties-not-meeting-access"
            />
          )}
      </div>

      <QESCheckbox
        containerClassName="ms-1"
        label="Physician specialties"
        checked={rcContext.state.includePhysicianSpecialties}
        onChange={(e) => rcContext.dispatch({ payload: { includePhysicianSpecialties: e.target.checked }, type: ReportCustomizationReducerActions.ChangeIncludePhysicianSpecialties })}
        disabled={disableCurrentSpecialtySection}
        testId="report-customization__include-physician-specialties"
      />
      <QESCheckbox
        containerClassName="ms-1"
        label="Facility specialties"
        checked={rcContext.state.includeFacilitySpecialties}
        onChange={(e) => rcContext.dispatch({ payload: { includeFacilitySpecialties: e.target.checked }, type: ReportCustomizationReducerActions.ChangeIncludeFacilitySpecialties })}
        disabled={disableCurrentSpecialtySection}
        testId="report-customization__include-facility-specialties"
      />
      {rcContext.routeState.showExpandableSpecialties
        && (rcContext.state.reportType === ReportType.SpecialtySummaryByCounty
          || rcContext.state.reportType === ReportType.ZipClassAggregateResultsReport
          || rcContext.state.reportType === ReportType.ZipAnalysisResultsReport)
        && (
          <QESCheckbox
            containerClassName="ms-1"
            label="Expandable specialties"
            checked={rcContext.state.includeExpandableSpecialties}
            onChange={(e) => rcContext.dispatch({ payload: { includeExpandableSpecialties: e.target.checked }, type: ReportCustomizationReducerActions.ChangeIncludeExpandableSpecialties })}
            disabled={disableCurrentSpecialtySection}
            testId="report-customization__include-expandable-specialties"
          />
        )}
    </>
  )
}